import type { TruckServiceItemListQuery } from '@chukua/common';
import { Table, type TableColumnsType } from 'antd';
import { ClipboardList, SearchX } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import type { TruckServiceItemView } from '../truck-service.api';

interface TruckPlanTableProps {
  items: TruckServiceItemView[];
  total: number;
  columns: TableColumnsType<TruckServiceItemView>;
  list: ListQueryControls<TruckServiceItemListQuery>;
  isRefreshing: boolean;
  /** Shown when the truck has no items at all, naming where to fix that. */
  emptyDescription: string;
}

/** Search box plus the server-paged list of a truck's service items, on both of its tabs; the old rows stay up, dimmed, while the next page loads. */
export function TruckPlanTable({ items, total, columns, list, isRefreshing, emptyDescription }: TruckPlanTableProps) {
  const isEmptyPlan = total === 0 && !list.isFiltered;

  return (
    <div className="flex flex-col gap-4">
      {!isEmptyPlan && <ListSearch value={list.query.q} placeholder="Search code or name" onSearch={(q) => list.setQuery({ q }, { replace: true })} />}
      {isEmptyPlan ? (
        <EmptyState icon={ClipboardList} title="Nothing scheduled for this truck" description={emptyDescription} />
      ) : (
        <Table
          rowKey="serviceItemId"
          dataSource={items}
          columns={columns}
          loading={isRefreshing}
          scroll={{ x: 'max-content' }}
          locale={
            list.isFiltered
              ? { emptyText: <EmptyState icon={SearchX} title="No matching service items" description="No items on this plan match." /> }
              : undefined
          }
          {...list.tableProps<TruckServiceItemView>(total)}
        />
      )}
    </div>
  );
}
